import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { KpiCard } from "../components/dashboard/KpiCard";
import {
  ShieldCheck,
  FileText,
  Building2,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Cell,
} from "recharts";
import { FRAMEWORK_COLORS } from "../contexts/ComplianceContext";
import { cn } from "../lib/utils";

const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 8,
    fontSize: 12,
    color: "hsl(var(--foreground))",
  },
  labelStyle: { color: "hsl(var(--foreground))", fontWeight: 600 },
};

export default function ComplianceDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/compliance/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="card-surface h-28 animate-pulse" />
        ))}
      </div>
    );
  }

  const t = data.totals;
  const radarData = data.frameworks.map((f) => ({
    framework: f.code,
    "Control %": f.control_completion,
    "Evidence %": f.evidence_completion,
    "Assessment %": f.assessment_compliance,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compliance Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cross-framework posture, department accountability, and evidence coverage — real-time.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          testId="cdash-control-completion"
          title="Control Completion"
          value={t.control_completion}
          suffix="%"
          icon={ShieldCheck}
          kind="info"
          delta={`${t.total_controls} controls in library`}
        />
        <KpiCard
          testId="cdash-evidence-completion"
          title="Evidence Completion"
          value={t.evidence_completion}
          suffix="%"
          icon={FileText}
          kind="success"
          delta={`${t.approved_evidence}/${t.total_evidence} approved`}
        />
        <KpiCard
          testId="cdash-pending-evidence"
          title="Pending Evidence"
          value={t.pending_evidence}
          icon={Clock}
          kind="warning"
          delta="Awaiting approval"
        />
        <KpiCard
          testId="cdash-expiring"
          title="Expiring Soon"
          value={t.expiring_soon}
          icon={AlertTriangle}
          kind="critical"
          delta="Within 30 days"
        />
      </div>

      {/* Framework comparison bars */}
      <Card data-testid="framework-comparison">
        <CardHeader>
          <CardTitle>Framework comparison</CardTitle>
          <CardDescription>Control implementation, evidence coverage, and latest self-assessment score by framework.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.frameworks} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                domain={[0, 100]}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: "hsl(var(--muted))" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Bar dataKey="control_completion" name="Control %" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={24} />
              <Bar dataKey="evidence_completion" name="Evidence %" fill="#16A34A" radius={[4, 4, 0, 0]} maxBarSize={24} />
              <Bar dataKey="assessment_compliance" name="Assessment %" fill="#D97706" radius={[4, 4, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Framework detail cards */}
        <Card className="lg:col-span-2" data-testid="framework-postures">
          <CardHeader>
            <CardTitle>Framework posture</CardTitle>
            <CardDescription>Overall compliance breakdown per framework.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {data.frameworks.map((f) => {
                const color = FRAMEWORK_COLORS[f.code] || "#2563EB";
                return (
                  <li key={f.id} className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-9 h-9 rounded-md flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium truncate">{f.name}</div>
                          <span className="tabular-nums text-sm font-semibold" style={{ color }}>
                            {f.control_completion}%
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${f.control_completion}%`, backgroundColor: color }} />
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-3 text-[11px]">
                          <StatMini label="Controls" value={`${f.controls_implemented}/${f.controls_total}`} />
                          <StatMini label="Evidence" value={`${f.evidence_completion}%`} />
                          <StatMini label="Assessment" value={`${f.assessment_compliance}%`} />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
              {data.frameworks.length === 0 && (
                <li className="py-8 text-center text-muted-foreground">No frameworks configured.</li>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Radar */}
        <Card data-testid="framework-radar">
          <CardHeader>
            <CardTitle>Coverage radar</CardTitle>
            <CardDescription>3-axis view per framework.</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="framework" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <Radar name="Control %" dataKey="Control %" stroke="#2563EB" fill="#2563EB" fillOpacity={0.25} />
                <Radar name="Evidence %" dataKey="Evidence %" stroke="#16A34A" fill="#16A34A" fillOpacity={0.20} />
                <Radar name="Assessment %" dataKey="Assessment %" stroke="#D97706" fill="#D97706" fillOpacity={0.15} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Tooltip {...TOOLTIP_STYLE} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Department compliance */}
      <Card data-testid="department-compliance">
        <CardHeader>
          <CardTitle>Department compliance</CardTitle>
          <CardDescription>Percentage of controls implemented, grouped by owning department.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.departments.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Building2 className="w-8 h-8 mx-auto mb-2" />
              No department data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, data.departments.length * 40)}>
              <BarChart
                data={data.departments}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 10, bottom: 0 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  cursor={{ fill: "hsl(var(--muted))" }}
                  formatter={(value, name, ctx) => [
                    `${value}%  (${ctx.payload.implemented}/${ctx.payload.total})`,
                    "Compliance",
                  ]}
                />
                <Bar dataKey="compliance" radius={[0, 6, 6, 0]} maxBarSize={26}>
                  {data.departments.map((d) => (
                    <Cell
                      key={d.name || d.id || d.department}
                      fill={d.compliance >= 75 ? "#16A34A" : d.compliance >= 40 ? "#D97706" : "#DC2626"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground uppercase tracking-wider text-[10px]">{label}</div>
      <div className="font-medium text-foreground">{value}</div>
    </div>
  );
}
